/**
 * Research History API
 * Endpoint: GET /sellers-agents/analysis-history
 */

import { apiClient } from '@/auth/apiClient';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ResearchHistoryNiche {
    id: string | number;
    name: string;
    niche_score: number | null;
    is_favourite: boolean;
}

export interface ResearchHistoryItem {
    id: string | number;
    created_at: string;
    status: string;
    niches: ResearchHistoryNiche[];
}

export interface ResearchHistoryResponse {
    items: ResearchHistoryItem[];
    total: number;
    skip: number;
    limit: number;
}

export interface ResearchHistoryParams {
    sort?: 'asc' | 'desc';
    period_start?: string;
    period_end?: string;
    only_favourites?: boolean;
    search?: string;
    skip?: number;
    limit?: number;
    status?: string;
    marketplace?: string;
}

// ── API Service ─────────────────────────────────────────────────────────────

export class ResearchHistoryApiService {
    private static readonly BASE_PATH = '/sellers-agents/analysis-history';

    static async fetchHistory(params: ResearchHistoryParams = {}): Promise<ResearchHistoryResponse> {
        const response = await apiClient.get<ResearchHistoryResponse>(
            this.BASE_PATH,
            params as Record<string, unknown>
        );
        return response.data;
    }

    static async toggleFavourite(nicheId: string | number, isFavourite: boolean): Promise<void> {
        if (isFavourite) {
            await apiClient.put(`/sellers-agents/niches/${nicheId}/favorite`);
        } else {
            await apiClient.delete(`/sellers-agents/niches/${nicheId}/favorite`);
        }
    }
}

// ── Legacy mock (kept for reference, unused) ──────────────────────────────────

const NICHE_NAMES = [
    'Waterproof Backpack',
    'Hiking Daypack',
    'Travel Backpack',
    'Yoga Mat',
    'Resistance Bands',
    'Foam Roller',
    'Camping Lantern',
    'Portable Charger',
    'Laptop Stand',
    'Desk Organizer',
    'Air Purifier',
    'Smart Water Bottle',
    'Running Belt',
    'Compression Socks',
    'Kitchen Scale',
    'Coffee Grinder',
];

function randomScore() {
    // Spread across tiers: recommended (7-10), borderline (4-7), not recommended (1-4)
    const v = Math.random();
    if (v > 0.6) return +(6 + Math.random() * 4).toFixed(1);
    if (v > 0.3) return +(3.5 + Math.random() * 3.5).toFixed(1);
    return +(1 + Math.random() * 3).toFixed(1);
}

function randomDate(daysAgo: number) {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString();
}

function buildMockItems(count: number, offset: number): ResearchHistoryItem[] {
    return Array.from({ length: count }, (_, i) => {
        const idx = offset + i;
        const nicheCount = (idx % 3) + 1; // 1, 2, or 3 niches per analysis
        const niches: ResearchHistoryNiche[] = Array.from({ length: nicheCount }, (__, j) => ({
            id: `${idx}-${j}`,
            name: NICHE_NAMES[(idx + j) % NICHE_NAMES.length],
            niche_score: randomScore(),
            is_favourite: false,
        }));

        return {
            id: `mock-${idx}`,
            created_at: randomDate(idx * 2),
            status: idx % 10 === 0 ? 'processing' : 'completed',
            niches,
        };
    });
}

const MOCK_TOTAL = 47;
const MOCK_PAGE_SIZE = 15;


